// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        client: "h-14 px-8 text-lg font-semibold [&_svg]:size-5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/input.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Input = React2.forwardRef(
  ({ autoComplete, className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "input",
      {
        autoComplete: autoComplete ?? "off",
        type,
        className: cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/ui/label.tsx
import * as React3 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx3 } from "react/jsx-runtime";
var labelVariants = cva2(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/ui/card.tsx
import * as React4 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Card = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn("font-semibold leading-none tracking-tight", className),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/components/ui/badge.tsx
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx5 } from "react/jsx-runtime";
var badgeVariants = cva3(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx5("div", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/components/ui/alert.tsx
import * as React5 from "react";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var alertVariants = cva4(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        warning: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400",
        success: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/50 dark:text-emerald-200 [&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400",
        info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/50 dark:text-blue-200 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React5.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx6(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React5.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx6(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props,
    children
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";

// src/components/ui/alert-dialog.tsx
import * as React6 from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { jsx as jsx7, jsxs } from "react/jsx-runtime";
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  AlertDialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
var AlertDialogContent = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
  /* @__PURE__ */ jsx7(AlertDialogOverlay, {}),
  /* @__PURE__ */ jsx7(
    AlertDialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props
    }
  )
] }));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
var AlertDialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx7(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
AlertDialogHeader.displayName = "AlertDialogHeader";
var AlertDialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx7(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
AlertDialogFooter.displayName = "AlertDialogFooter";
var AlertDialogTitle = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
var AlertDialogDescription = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
var AlertDialogAction = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(buttonVariants(), className),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
var AlertDialogCancel = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

// src/components/ui/avatar.tsx
import * as React7 from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx8 } from "react/jsx-runtime";
var Avatar = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    ),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full w-full", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// src/components/ui/avatar-upload.tsx
import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";

// src/components/ui/dialog.tsx
import * as React8 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { jsx as jsx9, jsxs as jsxs2 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React8.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs2(DialogPortal, { children: [
  /* @__PURE__ */ jsx9(DialogOverlay, {}),
  /* @__PURE__ */ jsxs2(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs2(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", children: [
          /* @__PURE__ */ jsx9(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx9("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx9(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx9(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/ui/avatar-upload.tsx
import { Loader2, Upload, Trash2 } from "lucide-react";
import { Fragment, jsx as jsx10, jsxs as jsxs3 } from "react/jsx-runtime";
var ACCEPTED_TYPES = "image/png,image/jpeg,image/webp";
function getInitials(name) {
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}
async function cropImage(imageSrc, crop) {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = reject;
    image.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const size = Math.min(crop.width, crop.height, 256);
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    size,
    size
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create blob"));
      },
      "image/jpeg",
      0.85
    );
  });
}
function AvatarUpload({
  currentAvatarUrl,
  userName,
  onUploadComplete,
  onRemove,
  generateUploadUrl,
  labels
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const onCropComplete = useCallback(
    (_, croppedAreaPixels) => {
      setCroppedArea(croppedAreaPixels);
    },
    []
  );
  const handleFileSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setDialogOpen(true);
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    []
  );
  const handleCropConfirm = useCallback(async () => {
    if (!imageSrc || !croppedArea) return;
    setIsUploading(true);
    try {
      const blob = await cropImage(imageSrc, croppedArea);
      const uploadUrl = await generateUploadUrl();
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "image/jpeg" },
        body: blob
      });
      if (!response.ok) throw new Error("Upload failed");
      const { storageId } = await response.json();
      await onUploadComplete(storageId);
      setDialogOpen(false);
      setImageSrc(null);
    } catch (err) {
      console.error("Avatar upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  }, [imageSrc, croppedArea, generateUploadUrl, onUploadComplete]);
  const handleRemove = useCallback(async () => {
    setIsRemoving(true);
    try {
      await onRemove();
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove]);
  const handleDialogClose = useCallback(() => {
    if (!isUploading) {
      setDialogOpen(false);
      setImageSrc(null);
    }
  }, [isUploading]);
  return /* @__PURE__ */ jsxs3(Fragment, { children: [
    /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-4", children: [
      /* @__PURE__ */ jsxs3(Avatar, { className: "h-16 w-16", children: [
        currentAvatarUrl && /* @__PURE__ */ jsx10(AvatarImage, { src: currentAvatarUrl, alt: userName }),
        /* @__PURE__ */ jsx10(AvatarFallback, { className: "text-lg font-semibold", children: getInitials(userName || "U") })
      ] }),
      /* @__PURE__ */ jsx10("div", { className: "flex flex-col gap-2", children: /* @__PURE__ */ jsxs3("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsxs3(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => fileInputRef.current?.click(),
            disabled: isUploading || isRemoving,
            children: [
              /* @__PURE__ */ jsx10(Upload, { className: "mr-2 h-4 w-4" }),
              labels?.upload ?? "Upload avatar"
            ]
          }
        ),
        currentAvatarUrl && /* @__PURE__ */ jsxs3(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: handleRemove,
            disabled: isUploading || isRemoving,
            children: [
              isRemoving ? /* @__PURE__ */ jsx10(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx10(Trash2, { className: "mr-2 h-4 w-4" }),
              labels?.remove ?? "Remove"
            ]
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx10(
        "input",
        {
          ref: fileInputRef,
          type: "file",
          accept: ACCEPTED_TYPES,
          onChange: handleFileSelect,
          className: "hidden"
        }
      )
    ] }),
    /* @__PURE__ */ jsx10(Dialog, { open: dialogOpen, onOpenChange: handleDialogClose, children: /* @__PURE__ */ jsxs3(DialogContent, { className: "sm:max-w-md", children: [
      /* @__PURE__ */ jsx10(DialogHeader, { children: /* @__PURE__ */ jsx10(DialogTitle, { children: labels?.cropTitle ?? "Crop avatar" }) }),
      /* @__PURE__ */ jsx10("div", { className: "relative h-64 w-full", children: imageSrc && /* @__PURE__ */ jsx10(
        Cropper,
        {
          image: imageSrc,
          crop,
          zoom,
          aspect: 1,
          cropShape: "round",
          showGrid: false,
          onCropChange: setCrop,
          onZoomChange: setZoom,
          onCropComplete
        }
      ) }),
      /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-3 px-1", children: [
        /* @__PURE__ */ jsx10("span", { className: "text-xs text-muted-foreground", children: "Zoom" }),
        /* @__PURE__ */ jsx10(
          "input",
          {
            type: "range",
            min: 1,
            max: 3,
            step: 0.1,
            value: zoom,
            onChange: (e) => setZoom(Number(e.target.value)),
            className: "flex-1"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs3(DialogFooter, { children: [
        /* @__PURE__ */ jsx10(
          Button,
          {
            variant: "outline",
            onClick: handleDialogClose,
            disabled: isUploading,
            children: labels?.cropCancel ?? "Cancel"
          }
        ),
        /* @__PURE__ */ jsxs3(Button, { onClick: handleCropConfirm, disabled: isUploading, children: [
          isUploading && /* @__PURE__ */ jsx10(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }),
          labels?.cropConfirm ?? "Save"
        ] })
      ] })
    ] }) })
  ] });
}

// src/components/ui/separator.tsx
import * as React9 from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { jsx as jsx11 } from "react/jsx-runtime";
var Separator = React9.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx11(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator.displayName = SeparatorPrimitive.Root.displayName;

// src/components/ui/skeleton.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function Skeleton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: cn("animate-pulse rounded-md bg-primary/10", className),
      ...props
    }
  );
}

// src/components/ui/progress.tsx
import * as React10 from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { jsx as jsx13 } from "react/jsx-runtime";
var Progress = React10.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx13(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn(
      "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx13(
      ProgressPrimitive.Indicator,
      {
        className: "h-full w-full flex-1 bg-primary transition-all",
        style: { transform: `translateX(-${100 - (value || 0)}%)` }
      }
    )
  }
));
Progress.displayName = ProgressPrimitive.Root.displayName;

// src/components/ui/switch.tsx
import * as React11 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx14 } from "react/jsx-runtime";
var Switch = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx14(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx14(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/ui/select.tsx
import * as React12 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs4 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React12.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx15(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx15(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx15(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx15(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React12.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx15(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs4(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx15(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx15(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx15(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("px-2 py-1.5 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React12.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx15("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx15(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx15(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx15(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/ui/dropdown-menu.tsx
import * as React13 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check as Check2, ChevronRight, Circle } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs5 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuSubTrigger = React13.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx16(ChevronRight, { className: "ml-auto" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React13.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx16(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx16(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React13.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx16(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React13.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx16("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx16(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx16(Check2, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx16("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx16(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx16(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React13.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx16(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx16(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/components/ui/sheet.tsx
import * as React14 from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva as cva5 } from "class-variance-authority";
import { X as X2 } from "lucide-react";
import { jsx as jsx17, jsxs as jsxs6 } from "react/jsx-runtime";
var Sheet = SheetPrimitive.Root;
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(
  SheetPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetVariants = cva5(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
var SheetContent = React14.forwardRef(({ side = "right", className, children, hideOverlay, ...props }, ref) => /* @__PURE__ */ jsxs6(SheetPortal, { children: [
  !hideOverlay && /* @__PURE__ */ jsx17(SheetOverlay, {}),
  /* @__PURE__ */ jsxs6(
    SheetPrimitive.Content,
    {
      ref,
      className: cn(sheetVariants({ side }), className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs6(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary", children: [
          /* @__PURE__ */ jsx17(X2, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx17("span", { className: "sr-only", children: "Close" })
        ] }),
        children
      ]
    }
  )
] }));
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx17(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx17(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(
  SheetPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(
  SheetPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/components/ui/scroll-area.tsx
import * as React15 from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { jsx as jsx18, jsxs as jsxs7 } from "react/jsx-runtime";
var ScrollArea = React15.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs7(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx18(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
      /* @__PURE__ */ jsx18(ScrollBar, {}),
      /* @__PURE__ */ jsx18(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React15.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx18(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx18(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/components/ui/tooltip.tsx
import * as React16 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx19 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React16.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx19(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx19(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/components/ui/collapsible.tsx
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger2 = CollapsiblePrimitive.CollapsibleTrigger;
var CollapsibleContent2 = CollapsiblePrimitive.CollapsibleContent;

// src/components/ui/sidebar.tsx
import * as React18 from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";
import { cva as cva6 } from "class-variance-authority";
import { PanelLeft } from "lucide-react";

// src/hooks/use-mobile.ts
import * as React17 from "react";
var MOBILE_BREAKPOINT = 768;
function useIsMobile() {
  const [isMobile, setIsMobile] = React17.useState(void 0);
  React17.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return !!isMobile;
}

// src/components/ui/sidebar.tsx
import { jsx as jsx20, jsxs as jsxs8 } from "react/jsx-runtime";
var SIDEBAR_COOKIE_NAME = "sidebar:state";
var SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
var SIDEBAR_WIDTH = "16rem";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_WIDTH_ICON = "3rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
var SidebarContext = React18.createContext(null);
function useSidebar() {
  const context = React18.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
var SidebarProvider = React18.forwardRef(
  ({
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
    className,
    style,
    children,
    ...props
  }, ref) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React18.useState(false);
    const [_open, _setOpen] = React18.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React18.useCallback(
      (value) => {
        const openState = typeof value === "function" ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, open]
    );
    const toggleSidebar = React18.useCallback(() => {
      return isMobile ? setOpenMobile((open2) => !open2) : setOpen((open2) => !open2);
    }, [isMobile, setOpen, setOpenMobile]);
    React18.useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);
    const state = open ? "expanded" : "collapsed";
    const contextValue = React18.useMemo(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar
      }),
      [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
    );
    return /* @__PURE__ */ jsx20(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx20(TooltipProvider, { delayDuration: 0, children: /* @__PURE__ */ jsx20(
      "div",
      {
        style: {
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
          ...style
        },
        className: cn(
          "group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar",
          className
        ),
        ref,
        ...props,
        children
      }
    ) }) });
  }
);
SidebarProvider.displayName = "SidebarProvider";
var Sidebar = React18.forwardRef(
  ({
    side = "left",
    variant = "sidebar",
    collapsible = "offcanvas",
    className,
    children,
    ...props
  }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
    if (collapsible === "none") {
      return /* @__PURE__ */ jsx20(
        "div",
        {
          className: cn(
            "flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
            className
          ),
          ref,
          ...props,
          children
        }
      );
    }
    if (isMobile) {
      return /* @__PURE__ */ jsx20(Sheet, { open: openMobile, onOpenChange: setOpenMobile, ...props, children: /* @__PURE__ */ jsx20(
        SheetContent,
        {
          "data-sidebar": "sidebar",
          "data-mobile": "true",
          className: "w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden",
          style: {
            "--sidebar-width": SIDEBAR_WIDTH_MOBILE
          },
          side,
          children: /* @__PURE__ */ jsx20("div", { className: "flex h-full w-full flex-col", children })
        }
      ) });
    }
    return /* @__PURE__ */ jsxs8(
      "div",
      {
        ref,
        className: "group peer hidden md:block text-sidebar-foreground",
        "data-state": state,
        "data-collapsible": state === "collapsed" ? collapsible : "",
        "data-variant": variant,
        "data-side": side,
        children: [
          /* @__PURE__ */ jsx20(
            "div",
            {
              className: cn(
                "duration-200 relative h-svh bg-transparent transition-[width] ease-linear",
                "group-data-[collapsible=offcanvas]:!w-0",
                "group-data-[side=right]:rotate-180",
                state === "collapsed" && collapsible === "icon" && "!w-12"
              ),
              style: { width: SIDEBAR_WIDTH }
            }
          ),
          /* @__PURE__ */ jsx20(
            "div",
            {
              className: cn(
                "duration-200 fixed inset-y-0 z-10 hidden h-svh transition-[left,right,width] ease-linear md:flex",
                side === "left" ? "left-0 group-data-[collapsible=offcanvas]:-left-64" : "right-0 group-data-[collapsible=offcanvas]:-right-64",
                // Adjust the padding for floating and inset variants.
                variant === "floating" || variant === "inset" ? "p-2" : "group-data-[side=left]:border-r group-data-[side=right]:border-l",
                state === "collapsed" && collapsible === "icon" && "!w-12",
                className
              ),
              style: { width: SIDEBAR_WIDTH },
              ...props,
              children: /* @__PURE__ */ jsx20(
                "div",
                {
                  "data-sidebar": "sidebar",
                  className: "flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow",
                  children
                }
              )
            }
          )
        ]
      }
    );
  }
);
Sidebar.displayName = "Sidebar";
var SidebarTrigger = React18.forwardRef(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsxs8(
    Button,
    {
      ref,
      "data-sidebar": "trigger",
      variant: "ghost",
      size: "icon",
      className: cn("h-7 w-7", className),
      onClick: (event) => {
        onClick?.(event);
        toggleSidebar();
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx20(PanelLeft, {}),
        /* @__PURE__ */ jsx20("span", { className: "sr-only", children: "Toggle Sidebar" })
      ]
    }
  );
});
SidebarTrigger.displayName = "SidebarTrigger";
var SidebarRail = React18.forwardRef(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsx20(
    "button",
    {
      ref,
      "data-sidebar": "rail",
      "aria-label": "Toggle Sidebar",
      tabIndex: -1,
      onClick: toggleSidebar,
      title: "Toggle Sidebar",
      className: cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      ),
      ...props
    }
  );
});
SidebarRail.displayName = "SidebarRail";
var SidebarInset = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    "main",
    {
      ref,
      className: cn(
        "relative flex min-h-svh flex-1 flex-col bg-background",
        "peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
        className
      ),
      ...props
    }
  );
});
SidebarInset.displayName = "SidebarInset";
var SidebarInput = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    Input,
    {
      ref,
      "data-sidebar": "input",
      className: cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className
      ),
      ...props
    }
  );
});
SidebarInput.displayName = "SidebarInput";
var SidebarHeader = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref,
      "data-sidebar": "header",
      className: cn("flex flex-col gap-2 p-2", className),
      ...props
    }
  );
});
SidebarHeader.displayName = "SidebarHeader";
var SidebarFooter = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref,
      "data-sidebar": "footer",
      className: cn("flex flex-col gap-2 p-2", className),
      ...props
    }
  );
});
SidebarFooter.displayName = "SidebarFooter";
var SidebarSeparator = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    Separator,
    {
      ref,
      "data-sidebar": "separator",
      className: cn("mx-2 w-auto bg-sidebar-border", className),
      ...props
    }
  );
});
SidebarSeparator.displayName = "SidebarSeparator";
var SidebarContent = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref,
      "data-sidebar": "content",
      className: cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      ),
      ...props
    }
  );
});
SidebarContent.displayName = "SidebarContent";
var SidebarGroup = React18.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref,
      "data-sidebar": "group",
      className: cn("relative flex w-full min-w-0 flex-col p-2", className),
      ...props
    }
  );
});
SidebarGroup.displayName = "SidebarGroup";
var SidebarGroupLabel = React18.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot2 : "div";
  return /* @__PURE__ */ jsx20(
    Comp,
    {
      ref,
      "data-sidebar": "group-label",
      className: cn(
        "duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      ),
      ...props
    }
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";
var SidebarGroupAction = React18.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot2 : "button";
  return /* @__PURE__ */ jsx20(
    Comp,
    {
      ref,
      "data-sidebar": "group-action",
      className: cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";
var SidebarGroupContent = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
  "div",
  {
    ref,
    "data-sidebar": "group-content",
    className: cn("w-full text-sm", className),
    ...props
  }
));
SidebarGroupContent.displayName = "SidebarGroupContent";
var SidebarMenu = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
  "ul",
  {
    ref,
    "data-sidebar": "menu",
    className: cn("flex w-full min-w-0 flex-col gap-1", className),
    ...props
  }
));
SidebarMenu.displayName = "SidebarMenu";
var SidebarMenuItem = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
  "li",
  {
    ref,
    "data-sidebar": "menu-item",
    className: cn("group/menu-item relative", className),
    ...props
  }
));
SidebarMenuItem.displayName = "SidebarMenuItem";
var sidebarMenuButtonVariants = cva6(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var SidebarMenuButton = React18.forwardRef(
  ({
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    tooltip,
    className,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot2 : "button";
    const { isMobile, state } = useSidebar();
    const button = /* @__PURE__ */ jsx20(
      Comp,
      {
        ref,
        "data-sidebar": "menu-button",
        "data-size": size,
        "data-active": isActive,
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
        ...props
      }
    );
    if (!tooltip) {
      return button;
    }
    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip
      };
    }
    return /* @__PURE__ */ jsxs8(Tooltip, { children: [
      /* @__PURE__ */ jsx20(TooltipTrigger, { asChild: true, children: button }),
      /* @__PURE__ */ jsx20(
        TooltipContent,
        {
          side: "right",
          align: "center",
          hidden: state !== "collapsed" || isMobile,
          ...tooltip
        }
      )
    ] });
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";
var SidebarMenuAction = React18.forwardRef(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot2 : "button";
  return /* @__PURE__ */ jsx20(
    Comp,
    {
      ref,
      "data-sidebar": "menu-action",
      className: cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
        className
      ),
      ...props
    }
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";
var SidebarMenuBadge = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
  "div",
  {
    ref,
    "data-sidebar": "menu-badge",
    className: cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className
    ),
    ...props
  }
));
SidebarMenuBadge.displayName = "SidebarMenuBadge";
var SidebarMenuSkeleton = React18.forwardRef(({ className, showIcon = false, ...props }, ref) => {
  const width = React18.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      ref,
      "data-sidebar": "menu-skeleton",
      className: cn("rounded-md h-8 flex gap-2 px-2 items-center", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx20(
          Skeleton,
          {
            className: "size-4 rounded-md",
            "data-sidebar": "menu-skeleton-icon"
          }
        ),
        /* @__PURE__ */ jsx20(
          Skeleton,
          {
            className: "h-4 flex-1 max-w-[--skeleton-width]",
            "data-sidebar": "menu-skeleton-text",
            style: {
              "--skeleton-width": width
            }
          }
        )
      ]
    }
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";
var SidebarMenuSub = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
  "ul",
  {
    ref,
    "data-sidebar": "menu-sub",
    className: cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className
    ),
    ...props
  }
));
SidebarMenuSub.displayName = "SidebarMenuSub";
var SidebarMenuSubItem = React18.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx20("li", { ref, ...props }));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";
var SidebarMenuSubButton = React18.forwardRef(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot2 : "a";
  return /* @__PURE__ */ jsx20(
    Comp,
    {
      ref,
      "data-sidebar": "menu-sub-button",
      "data-size": size,
      "data-active": isActive,
      className: cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

// src/components/ui/toast.tsx
import * as React19 from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva as cva7 } from "class-variance-authority";
import { X as X3 } from "lucide-react";
import { jsx as jsx21 } from "react/jsx-runtime";
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = cva7(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive: "destructive group border-destructive bg-destructive text-destructive-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast = React19.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx21(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx21(X3, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm font-semibold [&+div]:text-xs", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-sm opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// src/hooks/use-toast.ts
import * as React20 from "react";
var TOAST_LIMIT = 1;
var TOAST_REMOVE_DELAY = 1e6;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
var addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
var reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
function useToast() {
  const [state, setState] = React20.useState(memoryState);
  React20.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}

// src/components/ui/toaster.tsx
import { Fragment as Fragment2, jsx as jsx22, jsxs as jsxs9 } from "react/jsx-runtime";
function Toaster() {
  const { toasts } = useToast();
  return /* @__PURE__ */ jsxs9(Fragment2, { children: [
    toasts.map(({ id, title, description, action, ...props }) => /* @__PURE__ */ jsxs9(Toast, { ...props, children: [
      /* @__PURE__ */ jsxs9("div", { className: "grid gap-1", children: [
        title ? /* @__PURE__ */ jsx22(ToastTitle, { children: title }) : null,
        description ? /* @__PURE__ */ jsx22(ToastDescription, { children: description }) : null
      ] }),
      action,
      /* @__PURE__ */ jsx22(ToastClose, {})
    ] }, id)),
    /* @__PURE__ */ jsx22(ToastViewport, {})
  ] });
}

// src/components/ui/popover.tsx
import * as React21 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx23 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverContent = React21.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx23(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx23(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/ui/calendar.tsx
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight as ChevronRight2 } from "lucide-react";
import { jsx as jsx24 } from "react/jsx-runtime";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx24(
    DayPicker,
    {
      showOutsideDays,
      navLayout: props.navLayout ?? "around",
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col gap-4 sm:flex-row sm:gap-6",
        month: "relative flex flex-col gap-4",
        month_caption: "flex h-10 items-center justify-center px-10",
        caption_label: "inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-foreground",
        dropdowns: "flex items-center gap-2",
        dropdown_root: "relative inline-flex h-9 items-center rounded-full border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
        dropdown: "absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed",
        months_dropdown: "min-w-[7.5rem]",
        years_dropdown: "min-w-[5.5rem]",
        nav: "absolute inset-x-0 top-0 flex items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "absolute left-0 top-0 h-9 w-9 rounded-full border-border bg-background/95 p-0 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "absolute right-0 top-0 h-9 w-9 rounded-full border-border bg-background/95 p-0 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
        ),
        chevron: "h-4 w-4 shrink-0 text-muted-foreground",
        month_grid: "w-full border-collapse",
        weekdays: "border-b border-border/60",
        weekday: "h-10 px-0 text-center align-middle text-xs font-medium tracking-wide text-muted-foreground",
        weeks: "mt-2",
        week: "",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent/70 [&:has([aria-selected].day-range-end)]:rounded-r-full [&:has([aria-selected].day-outside)]:bg-accent/30",
          props.mode === "range" ? "[&:has(>.day-range-end)]:rounded-r-full [&:has(>.day-range-start)]:rounded-l-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full" : "[&:has([aria-selected])]:rounded-full"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-10 w-10 rounded-full p-0 text-sm font-medium text-foreground aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground"
        ),
        range_end: "day-range-end",
        range_start: "day-range-start",
        selected: "bg-primary text-primary-foreground hover:bg-primary/95 hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground ring-1 ring-border ring-inset",
        outside: "day-outside text-muted-foreground/60 aria-selected:text-muted-foreground/70",
        disabled: "text-muted-foreground/45 opacity-100",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation }) => {
          const Icon2 = orientation === "left" ? ChevronLeft : ChevronRight2;
          return /* @__PURE__ */ jsx24(Icon2, { className: "h-4 w-4" });
        }
      },
      ...props
    }
  );
}

// src/components/ui/birthday-picker.tsx
import * as React22 from "react";
import { format, parse, isValid, differenceInYears, isFuture } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { jsx as jsx25, jsxs as jsxs10 } from "react/jsx-runtime";
var MIN_AGE = 18;
var MAX_AGE = 120;
function validateBirthdate(date) {
  if (isFuture(date)) {
    return "Das Geburtsdatum darf nicht in der Zukunft liegen.";
  }
  const age = differenceInYears(/* @__PURE__ */ new Date(), date);
  if (age < MIN_AGE) {
    return `Sie m\xFCssen mindestens ${MIN_AGE} Jahre alt sein.`;
  }
  if (age > MAX_AGE) {
    return "Bitte \xFCberpr\xFCfen Sie das eingegebene Geburtsdatum.";
  }
  return null;
}
function BirthdayPicker({
  value,
  onChange,
  className,
  id
}) {
  const [open, setOpen] = React22.useState(false);
  const [validationError, setValidationError] = React22.useState(
    null
  );
  const selectedDate = value ? parse(value, "yyyy-MM-dd", /* @__PURE__ */ new Date()) : void 0;
  const isSelectedValid = selectedDate && isValid(selectedDate);
  const today = /* @__PURE__ */ new Date();
  const handleSelect = (date) => {
    if (!date) return;
    const error = validateBirthdate(date);
    setValidationError(error);
    if (!error) {
      onChange(format(date, "yyyy-MM-dd"));
      setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs10("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsxs10(Popover, { open, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx25(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs10(
        "button",
        {
          id,
          type: "button",
          className: `${className ?? ""} flex items-center gap-3 text-left ${!isSelectedValid ? "text-muted-foreground" : ""}`,
          children: [
            /* @__PURE__ */ jsx25(CalendarIcon, { className: "h-5 w-5 shrink-0 text-muted-foreground" }),
            isSelectedValid ? format(selectedDate, "dd. MMMM yyyy", { locale: de }) : "Geburtsdatum w\xE4hlen"
          ]
        }
      ) }),
      /* @__PURE__ */ jsx25(
        PopoverContent,
        {
          className: "w-auto rounded-[1.25rem] border-border/80 bg-popover p-2 shadow-xl",
          align: "start",
          children: /* @__PURE__ */ jsx25(
            Calendar,
            {
              mode: "single",
              selected: isSelectedValid ? selectedDate : void 0,
              onSelect: handleSelect,
              captionLayout: "dropdown",
              className: "rounded-2xl",
              fromYear: today.getFullYear() - MAX_AGE,
              toYear: today.getFullYear() - MIN_AGE,
              defaultMonth: isSelectedValid ? selectedDate : new Date(1990, 0),
              locale: de,
              showOutsideDays: false,
              disabled: { after: today }
            }
          )
        }
      )
    ] }),
    validationError && /* @__PURE__ */ jsx25("p", { className: "text-sm text-destructive", children: validationError })
  ] });
}

// src/components/ErrorBoundary.tsx
import * as React23 from "react";

// src/components/ErrorFallback.tsx
import { jsx as jsx26, jsxs as jsxs11 } from "react/jsx-runtime";
function ErrorFallback({ error, onReset }) {
  return /* @__PURE__ */ jsx26(
    "div",
    {
      role: "alert",
      className: "flex min-h-screen items-center justify-center bg-background p-4",
      children: /* @__PURE__ */ jsxs11("div", { className: "max-w-md w-full text-center", children: [
        /* @__PURE__ */ jsx26("div", { className: "w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6", children: /* @__PURE__ */ jsxs11(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            width: "32",
            height: "32",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            className: "text-destructive",
            children: [
              /* @__PURE__ */ jsx26("path", { d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" }),
              /* @__PURE__ */ jsx26("path", { d: "M12 9v4" }),
              /* @__PURE__ */ jsx26("path", { d: "M12 17h.01" })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx26("h1", { className: "text-xl font-semibold mb-2", children: "Ein Fehler ist aufgetreten" }),
        /* @__PURE__ */ jsx26("p", { className: "text-muted-foreground mb-6", children: "Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu." }),
        error && /* @__PURE__ */ jsxs11("details", { className: "text-left mb-6", children: [
          /* @__PURE__ */ jsx26("summary", { className: "text-xs text-muted-foreground cursor-pointer", children: "Technische Details" }),
          /* @__PURE__ */ jsx26("pre", { className: "text-xs bg-muted p-3 rounded-lg mt-2 overflow-auto max-h-32", children: error.message })
        ] }),
        /* @__PURE__ */ jsx26("div", { className: "flex gap-3 justify-center", children: /* @__PURE__ */ jsx26(
          Button,
          {
            onClick: () => {
              onReset?.();
              window.location.reload();
            },
            children: "Seite neu laden"
          }
        ) })
      ] })
    }
  );
}

// src/components/ErrorBoundary.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var ErrorBoundary = class extends React23.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }
  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };
  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? /* @__PURE__ */ jsx27(
        ErrorFallback,
        {
          error: this.state.error,
          onReset: this.handleReset
        }
      );
    }
    return this.props.children;
  }
};

// src/components/SkipLink.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
function SkipLink({
  targetId = "main-content",
  label = "Zum Hauptinhalt springen"
}) {
  return /* @__PURE__ */ jsx28(
    "a",
    {
      href: `#${targetId}`,
      className: "sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-white focus:text-black focus:underline focus:top-2 focus:left-2 focus:rounded focus:shadow-lg",
      children: label
    }
  );
}

// src/components/video/ParticipantTile.tsx
import * as React24 from "react";
import { ParticipantEvent, Track } from "livekit-client";
import { MicOff } from "lucide-react";
import { jsx as jsx29, jsxs as jsxs12 } from "react/jsx-runtime";
function ParticipantTile({
  participant,
  isSpotlight = false,
  showName = true,
  className
}) {
  const videoRef = React24.useRef(null);
  const audioRef = React24.useRef(null);
  const [isMuted, setIsMuted] = React24.useState(false);
  const [isVideoOff, setIsVideoOff] = React24.useState(false);
  const [trackUpdate, setTrackUpdate] = React24.useState(0);
  React24.useEffect(() => {
    const handleTrackChange = () => setTrackUpdate((n) => n + 1);
    participant.on(ParticipantEvent.TrackPublished, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnpublished, handleTrackChange);
    participant.on(ParticipantEvent.TrackSubscribed, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnsubscribed, handleTrackChange);
    participant.on(ParticipantEvent.TrackMuted, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnmuted, handleTrackChange);
    participant.on(ParticipantEvent.LocalTrackPublished, handleTrackChange);
    participant.on(ParticipantEvent.LocalTrackUnpublished, handleTrackChange);
    return () => {
      participant.off(ParticipantEvent.TrackPublished, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnpublished, handleTrackChange);
      participant.off(ParticipantEvent.TrackSubscribed, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnsubscribed, handleTrackChange);
      participant.off(ParticipantEvent.TrackMuted, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnmuted, handleTrackChange);
      participant.off(ParticipantEvent.LocalTrackPublished, handleTrackChange);
      participant.off(ParticipantEvent.LocalTrackUnpublished, handleTrackChange);
    };
  }, [participant]);
  React24.useEffect(() => {
    const videoTrack = participant.getTrackPublication(Track.Source.Camera);
    const audioTrack = participant.getTrackPublication(Track.Source.Microphone);
    if (videoTrack?.track && videoRef.current) {
      videoTrack.track.attach(videoRef.current);
    }
    if (audioTrack?.track && audioRef.current) {
      audioTrack.track.attach(audioRef.current);
    }
    setIsMuted(!audioTrack?.track || audioTrack?.isMuted || false);
    setIsVideoOff(!videoTrack?.track || videoTrack?.isMuted || false);
    return () => {
      videoTrack?.track?.detach();
      audioTrack?.track?.detach();
    };
  }, [participant, trackUpdate]);
  const getName = () => {
    if (participant.name) return participant.name;
    try {
      const metadata = JSON.parse(participant.metadata || "{}");
      return metadata.name || participant.identity;
    } catch {
      return participant.identity;
    }
  };
  const getQualityIndicator = () => {
    const quality = participant.connectionQuality;
    const dots = quality === "excellent" ? 3 : quality === "good" ? 2 : 1;
    return /* @__PURE__ */ jsx29("div", { className: "flex gap-0.5", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx29(
      "div",
      {
        className: cn(
          "w-1.5 h-1.5 rounded-full",
          i <= dots ? "bg-green-500" : "bg-stone-400"
        )
      },
      i
    )) });
  };
  return /* @__PURE__ */ jsxs12(
    "div",
    {
      className: cn(
        "relative overflow-hidden rounded-xl bg-stone-900",
        isSpotlight ? "aspect-video" : "aspect-video",
        className
      ),
      children: [
        /* @__PURE__ */ jsx29(
          "video",
          {
            ref: videoRef,
            autoPlay: true,
            playsInline: true,
            muted: true,
            className: cn(
              "absolute inset-0 w-full h-full",
              isSpotlight ? "object-contain" : "object-cover",
              isVideoOff && "hidden"
            )
          }
        ),
        /* @__PURE__ */ jsx29("audio", { ref: audioRef, autoPlay: true, playsInline: true, className: "hidden" }),
        isVideoOff && /* @__PURE__ */ jsx29("div", { className: "absolute inset-0 flex items-center justify-center bg-stone-800", children: /* @__PURE__ */ jsxs12("div", { className: "flex flex-col items-center gap-2", children: [
          /* @__PURE__ */ jsx29("div", { className: "w-16 h-16 rounded-full bg-sage-600 flex items-center justify-center text-white text-2xl font-semibold", children: getName().charAt(0).toUpperCase() }),
          /* @__PURE__ */ jsx29("span", { className: "text-stone-400 text-sm", children: "Kamera aus" })
        ] }) }),
        showName && /* @__PURE__ */ jsx29("div", { className: "absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent", children: /* @__PURE__ */ jsxs12("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs12("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx29("span", { className: "text-white text-sm font-medium truncate max-w-[150px]", children: getName() }),
            isMuted && /* @__PURE__ */ jsx29(MicOff, { className: "w-4 h-4 text-red-400" })
          ] }),
          getQualityIndicator()
        ] }) })
      ]
    }
  );
}

// src/components/video/ConnectionStatus.tsx
import * as React25 from "react";
import { Lock, LockOpen, Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx30, jsxs as jsxs13 } from "react/jsx-runtime";
var DEFAULT_LABELS = {
  encryptionActive: "Encryption active",
  notEncrypted: "Not encrypted",
  connecting: "Connecting...",
  reconnecting: "Reconnecting...",
  disconnected: "Disconnected",
  qualityExcellent: "Excellent",
  qualityGood: "Good",
  qualityPoor: "Poor",
  connected: "Connected"
};
function ConnectionStatus({
  status,
  connectionQuality = "unknown",
  startTime,
  isE2EEEnabled = false,
  labels: labelsProp,
  className
}) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [duration, setDuration] = React25.useState("00:00:00");
  React25.useEffect(() => {
    if (!startTime || status !== "connected") {
      setDuration("00:00:00");
      return;
    }
    const updateDuration = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1e3);
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor(elapsed % 3600 / 60);
      const seconds = elapsed % 60;
      setDuration(
        `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };
    updateDuration();
    const interval = setInterval(updateDuration, 1e3);
    return () => clearInterval(interval);
  }, [startTime, status]);
  const getQualityDisplay = () => {
    if (status === "connecting") {
      return {
        icon: /* @__PURE__ */ jsx30(Loader22, { className: "w-4 h-4 animate-spin" }),
        text: labels.connecting,
        color: "text-amber-500",
        bars: 0
      };
    }
    if (status === "reconnecting") {
      return {
        icon: /* @__PURE__ */ jsx30(Loader22, { className: "w-4 h-4 animate-spin" }),
        text: labels.reconnecting,
        color: "text-amber-500",
        bars: 0
      };
    }
    if (status === "disconnected") {
      return {
        icon: null,
        text: labels.disconnected,
        color: "text-red-500",
        bars: 0
      };
    }
    switch (connectionQuality) {
      case "excellent":
        return {
          icon: null,
          text: labels.qualityExcellent,
          color: "text-green-500",
          bars: 3
        };
      case "good":
        return {
          icon: null,
          text: labels.qualityGood,
          color: "text-green-500",
          bars: 2
        };
      case "poor":
        return {
          icon: null,
          text: labels.qualityPoor,
          color: "text-amber-500",
          bars: 1
        };
      default:
        return {
          icon: null,
          text: labels.connected,
          color: "text-green-500",
          bars: 0
        };
    }
  };
  const qualityDisplay = getQualityDisplay();
  return /* @__PURE__ */ jsxs13(
    "div",
    {
      className: cn(
        "flex items-center gap-4 px-4 py-2 bg-card/80 backdrop-blur rounded-full",
        className
      ),
      children: [
        isE2EEEnabled ? /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-1.5 text-sage-600", children: [
          /* @__PURE__ */ jsx30(Lock, { className: "w-4 h-4" }),
          /* @__PURE__ */ jsx30("span", { className: "text-sm font-medium", children: labels.encryptionActive })
        ] }) : /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-1.5 text-amber-600", children: [
          /* @__PURE__ */ jsx30(LockOpen, { className: "w-4 h-4" }),
          /* @__PURE__ */ jsx30("span", { className: "text-sm font-medium", children: labels.notEncrypted })
        ] }),
        /* @__PURE__ */ jsx30("div", { className: "w-px h-4 bg-border" }),
        /* @__PURE__ */ jsxs13("div", { className: cn("flex items-center gap-1.5", qualityDisplay.color), children: [
          qualityDisplay.icon,
          qualityDisplay.bars > 0 && /* @__PURE__ */ jsx30("div", { className: "flex items-end gap-0.5", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx30(
            "div",
            {
              className: cn(
                "w-1 rounded-sm",
                i === 1 ? "h-1.5" : i === 2 ? "h-2.5" : "h-3.5",
                i <= qualityDisplay.bars ? qualityDisplay.bars === 1 ? "bg-amber-500" : "bg-green-500" : "bg-muted-foreground/30"
              )
            },
            i
          )) }),
          /* @__PURE__ */ jsx30("span", { className: "text-sm", children: qualityDisplay.text })
        ] }),
        /* @__PURE__ */ jsx30("div", { className: "w-px h-4 bg-border" }),
        /* @__PURE__ */ jsx30("div", { className: "flex items-center gap-1.5 text-muted-foreground", children: /* @__PURE__ */ jsx30("span", { className: "text-sm font-mono", children: duration }) })
      ]
    }
  );
}

// src/components/video/ControlBar.tsx
import {
  Mic,
  MicOff as MicOff2,
  Video,
  VideoOff,
  Monitor,
  MonitorOff,
  PhoneOff
} from "lucide-react";
import { jsx as jsx31, jsxs as jsxs14 } from "react/jsx-runtime";
function ControlBar({
  isMicEnabled,
  isCameraEnabled,
  isScreenSharing,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onLeave,
  showScreenShare = true,
  leaveLabel = "Beenden",
  className,
  children
}) {
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: cn(
        "flex items-center justify-center gap-2 p-4 bg-card/80 backdrop-blur border-t",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs14("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs14(Tooltip, { children: [
            /* @__PURE__ */ jsx31(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx31(
              Button,
              {
                variant: isMicEnabled ? "secondary" : "destructive",
                size: "icon",
                className: "h-12 w-12 rounded-full",
                onClick: onToggleMic,
                "aria-label": isMicEnabled ? "Mikrofon stumm schalten" : "Mikrofon aktivieren",
                children: isMicEnabled ? /* @__PURE__ */ jsx31(Mic, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx31(MicOff2, { className: "h-5 w-5" })
              }
            ) }),
            /* @__PURE__ */ jsx31(TooltipContent, { children: isMicEnabled ? "Mikrofon stumm schalten" : "Mikrofon aktivieren" })
          ] }),
          /* @__PURE__ */ jsxs14(Tooltip, { children: [
            /* @__PURE__ */ jsx31(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx31(
              Button,
              {
                variant: isCameraEnabled ? "secondary" : "destructive",
                size: "icon",
                className: "h-12 w-12 rounded-full",
                onClick: onToggleCamera,
                "aria-label": isCameraEnabled ? "Kamera ausschalten" : "Kamera einschalten",
                children: isCameraEnabled ? /* @__PURE__ */ jsx31(Video, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx31(VideoOff, { className: "h-5 w-5" })
              }
            ) }),
            /* @__PURE__ */ jsx31(TooltipContent, { children: isCameraEnabled ? "Kamera ausschalten" : "Kamera einschalten" })
          ] }),
          showScreenShare && /* @__PURE__ */ jsxs14(Tooltip, { children: [
            /* @__PURE__ */ jsx31(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx31(
              Button,
              {
                variant: isScreenSharing ? "default" : "secondary",
                size: "icon",
                className: "h-12 w-12 rounded-full",
                onClick: onToggleScreenShare,
                "aria-label": isScreenSharing ? "Bildschirmfreigabe beenden" : "Bildschirm teilen",
                children: isScreenSharing ? /* @__PURE__ */ jsx31(MonitorOff, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx31(Monitor, { className: "h-5 w-5" })
              }
            ) }),
            /* @__PURE__ */ jsx31(TooltipContent, { children: isScreenSharing ? "Bildschirmfreigabe beenden" : "Bildschirm teilen" })
          ] }),
          children
        ] }),
        /* @__PURE__ */ jsx31("div", { className: "w-px h-8 bg-border mx-2" }),
        /* @__PURE__ */ jsxs14(Tooltip, { children: [
          /* @__PURE__ */ jsx31(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxs14(
            Button,
            {
              variant: "destructive",
              className: "h-12 px-6 rounded-full",
              onClick: onLeave,
              "aria-label": leaveLabel,
              children: [
                /* @__PURE__ */ jsx31(PhoneOff, { className: "h-5 w-5 mr-2" }),
                leaveLabel
              ]
            }
          ) }),
          /* @__PURE__ */ jsx31(TooltipContent, { children: leaveLabel })
        ] })
      ]
    }
  );
}

// src/components/video/ReconnectionOverlay.tsx
import { Loader2 as Loader23, RefreshCw, Phone } from "lucide-react";
import { Fragment as Fragment3, jsx as jsx32, jsxs as jsxs15 } from "react/jsx-runtime";
function ReconnectionOverlay({
  isReconnecting,
  attempt,
  maxAttempts,
  canManualRetry,
  lastError,
  onManualRetry,
  onContactSupport,
  className
}) {
  if (!isReconnecting && !canManualRetry) {
    return null;
  }
  return /* @__PURE__ */ jsx32(
    "div",
    {
      className: cn(
        "absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm",
        className
      ),
      children: /* @__PURE__ */ jsx32("div", { className: "bg-card rounded-2xl p-8 max-w-md mx-4 text-center shadow-xl", children: isReconnecting ? /* @__PURE__ */ jsxs15(Fragment3, { children: [
        /* @__PURE__ */ jsx32(Loader23, { className: "h-12 w-12 animate-spin mx-auto text-sage-500 mb-4" }),
        /* @__PURE__ */ jsx32("h2", { className: "text-xl font-semibold mb-2", children: "Verbindung wird wiederhergestellt" }),
        /* @__PURE__ */ jsxs15("p", { className: "text-muted-foreground mb-4", children: [
          "Versuch ",
          attempt,
          " von ",
          maxAttempts
        ] }),
        /* @__PURE__ */ jsx32("p", { className: "text-sm text-muted-foreground", children: "Ihr Gespr\xE4ch wird fortgesetzt, sobald die Verbindung wiederhergestellt ist." }),
        /* @__PURE__ */ jsx32("div", { className: "mt-6 w-full bg-muted rounded-full h-2", children: /* @__PURE__ */ jsx32(
          "div",
          {
            className: "bg-sage-500 h-2 rounded-full transition-all duration-300",
            style: { width: `${attempt / maxAttempts * 100}%` }
          }
        ) })
      ] }) : /* @__PURE__ */ jsxs15(Fragment3, { children: [
        /* @__PURE__ */ jsx32("div", { className: "w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4", children: /* @__PURE__ */ jsx32(RefreshCw, { className: "h-6 w-6 text-destructive" }) }),
        /* @__PURE__ */ jsx32("h2", { className: "text-xl font-semibold mb-2", children: "Verbindung fehlgeschlagen" }),
        /* @__PURE__ */ jsx32("p", { className: "text-muted-foreground mb-6", children: lastError || "Die automatische Wiederverbindung war nicht erfolgreich." }),
        /* @__PURE__ */ jsx32("p", { className: "text-sm text-muted-foreground mb-6", children: "Ihre Sitzung ist noch aktiv. Sie k\xF6nnen manuell einen neuen Verbindungsversuch starten." }),
        /* @__PURE__ */ jsxs15("div", { className: "flex gap-3 justify-center", children: [
          /* @__PURE__ */ jsxs15(Button, { onClick: onManualRetry, children: [
            /* @__PURE__ */ jsx32(RefreshCw, { className: "h-4 w-4 mr-2" }),
            "Erneut verbinden"
          ] }),
          onContactSupport && /* @__PURE__ */ jsxs15(Button, { variant: "outline", onClick: onContactSupport, children: [
            /* @__PURE__ */ jsx32(Phone, { className: "h-4 w-4 mr-2" }),
            "Support"
          ] })
        ] })
      ] }) })
    }
  );
}

// src/components/video/VideoStage.tsx
import * as React26 from "react";
import { Track as Track2, ParticipantEvent as ParticipantEvent2 } from "livekit-client";
import { User } from "lucide-react";
import { Fragment as Fragment4, jsx as jsx33, jsxs as jsxs16 } from "react/jsx-runtime";
function VideoStage({
  localParticipant,
  remoteParticipants,
  spotlightParticipant,
  isCameraEnabled = true,
  isMirrored = true,
  waitingMessage = "Warte auf andere Teilnehmer...",
  waitingSubtitle,
  localLabel = "Sie",
  className
}) {
  const extraRemoteParticipants = React26.useMemo(() => {
    return remoteParticipants.filter(
      (p) => p.sid !== spotlightParticipant?.sid
    );
  }, [remoteParticipants, spotlightParticipant]);
  return /* @__PURE__ */ jsxs16("div", { className: cn("relative flex-1 bg-stone-950", className), children: [
    /* @__PURE__ */ jsx33("div", { className: "absolute inset-0 bg-stone-900", children: spotlightParticipant ? /* @__PURE__ */ jsx33(
      ParticipantTile,
      {
        participant: spotlightParticipant,
        isSpotlight: true,
        className: "absolute inset-0 w-full h-full rounded-none"
      }
    ) : /* @__PURE__ */ jsx33("div", { className: "absolute inset-0 flex items-center justify-center bg-stone-800", children: /* @__PURE__ */ jsxs16("div", { className: "text-center p-8", children: [
      /* @__PURE__ */ jsx33(User, { className: "h-16 w-16 text-stone-600 mx-auto mb-4" }),
      /* @__PURE__ */ jsx33("p", { className: "text-stone-400", children: waitingMessage }),
      waitingSubtitle && /* @__PURE__ */ jsx33("p", { className: "text-stone-500 text-sm mt-2", children: waitingSubtitle })
    ] }) }) }),
    /* @__PURE__ */ jsxs16("div", { className: "absolute bottom-4 left-2 w-[100px] h-[75px] sm:left-4 sm:w-36 sm:h-28 md:w-48 md:h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700 z-10", children: [
      /* @__PURE__ */ jsx33(
        LocalPipTile,
        {
          participant: localParticipant,
          isCameraEnabled,
          isMirrored
        }
      ),
      /* @__PURE__ */ jsx33("div", { className: "absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent z-20", children: /* @__PURE__ */ jsx33("span", { className: "text-white text-xs", children: localLabel }) })
    ] }),
    extraRemoteParticipants.length > 0 && /* @__PURE__ */ jsx33("div", { className: "absolute bottom-4 left-[116px] sm:left-44 md:left-56 flex gap-2 sm:gap-3 z-10", children: extraRemoteParticipants.map((participant) => /* @__PURE__ */ jsx33(
      "div",
      {
        className: "w-[100px] h-[75px] sm:w-36 sm:h-28 md:w-48 md:h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700",
        children: /* @__PURE__ */ jsx33(
          ParticipantTile,
          {
            participant,
            className: "absolute inset-0 w-full h-full rounded-none",
            showName: true
          }
        )
      },
      participant.sid
    )) })
  ] });
}
function LocalPipTile({
  participant,
  isCameraEnabled,
  isMirrored
}) {
  const videoRef = React26.useRef(null);
  const streamRef = React26.useRef(null);
  const [hasVideo, setHasVideo] = React26.useState(false);
  const [hasLivekitTrack, setHasLivekitTrack] = React26.useState(false);
  React26.useEffect(() => {
    if (!participant) {
      setHasLivekitTrack(false);
      return;
    }
    const attachLivekitTrack = () => {
      const pub = participant.getTrackPublication(Track2.Source.Camera);
      if (pub?.track && videoRef.current) {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        pub.track.attach(videoRef.current);
        setHasLivekitTrack(true);
        setHasVideo(true);
      }
    };
    attachLivekitTrack();
    const onChange = () => attachLivekitTrack();
    participant.on(ParticipantEvent2.LocalTrackPublished, onChange);
    participant.on(ParticipantEvent2.LocalTrackUnpublished, () => {
      setHasLivekitTrack(false);
    });
    participant.on(ParticipantEvent2.TrackMuted, onChange);
    participant.on(ParticipantEvent2.TrackUnmuted, onChange);
    return () => {
      participant.off(ParticipantEvent2.LocalTrackPublished, onChange);
      participant.off(ParticipantEvent2.LocalTrackUnpublished, onChange);
      participant.off(ParticipantEvent2.TrackMuted, onChange);
      participant.off(ParticipantEvent2.TrackUnmuted, onChange);
    };
  }, [participant]);
  React26.useEffect(() => {
    if (hasLivekitTrack || !isCameraEnabled) return;
    let cancelled = false;
    async function startPreview() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setHasVideo(true);
      } catch {
        setHasVideo(false);
      }
    }
    startPreview();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [hasLivekitTrack, isCameraEnabled]);
  React26.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);
  return /* @__PURE__ */ jsxs16(Fragment4, { children: [
    /* @__PURE__ */ jsx33(
      "video",
      {
        ref: videoRef,
        autoPlay: true,
        playsInline: true,
        muted: true,
        className: cn(
          "absolute inset-0 w-full h-full object-cover",
          isMirrored && "-scale-x-100",
          (!hasVideo || !isCameraEnabled) && "hidden"
        )
      }
    ),
    (!hasVideo || !isCameraEnabled) && /* @__PURE__ */ jsx33("div", { className: "absolute inset-0 flex items-center justify-center bg-stone-800", children: /* @__PURE__ */ jsx33(User, { className: "h-8 w-8 text-stone-500" }) })
  ] });
}

// src/components/video/VideoErrorBoundary.tsx
import * as React27 from "react";
import { AlertTriangle, RefreshCw as RefreshCw2, Home } from "lucide-react";
import { jsx as jsx34, jsxs as jsxs17 } from "react/jsx-runtime";
var VideoErrorBoundary = class extends React27.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Video room error:", error, errorInfo);
  }
  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };
  render() {
    if (this.state.hasError) {
      return /* @__PURE__ */ jsx34(
        "div",
        {
          className: cn(
            "min-h-screen bg-stone-950 flex items-center justify-center p-4",
            this.props.className
          ),
          children: /* @__PURE__ */ jsxs17("div", { className: "bg-card rounded-2xl p-8 max-w-md text-center shadow-xl", children: [
            /* @__PURE__ */ jsx34("div", { className: "w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6", children: /* @__PURE__ */ jsx34(AlertTriangle, { className: "w-8 h-8 text-destructive" }) }),
            /* @__PURE__ */ jsx34("h2", { className: "text-xl font-semibold mb-2", children: "Ein Fehler ist aufgetreten" }),
            /* @__PURE__ */ jsx34("p", { className: "text-muted-foreground mb-6", children: "Bei der Video-Verbindung ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut." }),
            this.state.error && /* @__PURE__ */ jsxs17("details", { className: "text-left mb-6", children: [
              /* @__PURE__ */ jsx34("summary", { className: "text-xs text-muted-foreground cursor-pointer", children: "Technische Details" }),
              /* @__PURE__ */ jsx34("pre", { className: "text-xs bg-muted p-3 rounded-lg mt-2 overflow-auto max-h-32", children: this.state.error.message })
            ] }),
            /* @__PURE__ */ jsxs17("div", { className: "flex gap-3 justify-center", children: [
              /* @__PURE__ */ jsxs17(Button, { onClick: this.handleReset, children: [
                /* @__PURE__ */ jsx34(RefreshCw2, { className: "w-4 h-4 mr-2" }),
                "Erneut versuchen"
              ] }),
              this.props.onGoHome && /* @__PURE__ */ jsxs17(Button, { variant: "outline", onClick: this.props.onGoHome, children: [
                /* @__PURE__ */ jsx34(Home, { className: "w-4 h-4 mr-2" }),
                "Zur Startseite"
              ] })
            ] })
          ] })
        }
      );
    }
    return this.props.children;
  }
};

// src/components/video/VideoLoadingState.tsx
import { Loader2 as Loader24 } from "lucide-react";
import { jsx as jsx35, jsxs as jsxs18 } from "react/jsx-runtime";
function VideoLoadingState({
  message = "Verbindung wird hergestellt...",
  showDeviceCheck = false,
  className
}) {
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      className: cn(
        "min-h-screen bg-stone-950 flex flex-col items-center justify-center p-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs18("div", { className: "text-center", children: [
          /* @__PURE__ */ jsx35(Loader24, { className: "w-12 h-12 text-sage-500 animate-spin mx-auto mb-6" }),
          /* @__PURE__ */ jsx35("h2", { className: "text-xl font-semibold text-white mb-2", children: message }),
          showDeviceCheck && /* @__PURE__ */ jsxs18("div", { className: "space-y-2 text-muted-foreground text-sm", children: [
            /* @__PURE__ */ jsx35("p", { children: "Kamera und Mikrofon werden initialisiert..." }),
            /* @__PURE__ */ jsx35("p", { className: "text-xs", children: "Bitte stellen Sie sicher, dass die Browserberechtigungen erteilt wurden." })
          ] })
        ] }),
        /* @__PURE__ */ jsxs18("div", { className: "mt-8 w-full max-w-4xl", children: [
          /* @__PURE__ */ jsx35("div", { className: "aspect-video bg-stone-900 rounded-xl animate-pulse" }),
          /* @__PURE__ */ jsx35("div", { className: "mt-4 flex justify-center gap-4", children: [1, 2, 3, 4].map((i) => /* @__PURE__ */ jsx35(
            "div",
            {
              className: "w-12 h-12 bg-stone-800 rounded-full animate-pulse"
            },
            i
          )) })
        ] })
      ]
    }
  );
}

// src/components/video/BackgroundPicker.tsx
import { Check as Check3, Loader2 as Loader25, Ban, Sparkles } from "lucide-react";
import { jsx as jsx36, jsxs as jsxs19 } from "react/jsx-runtime";
function BackgroundPicker({
  mode,
  blurRadius,
  selectedPreset,
  presets,
  isSupported,
  isProcessing,
  onSelectNone,
  onSelectBlur,
  onSelectImage,
  onBlurRadiusChange
}) {
  if (!isSupported) {
    return /* @__PURE__ */ jsx36("div", { className: "rounded-md bg-amber-500/10 border border-amber-500/30 p-3", children: /* @__PURE__ */ jsx36("p", { className: "text-sm text-amber-400", children: "Ihr Browser unterst\xFCtzt keine Hintergrundeffekte." }) });
  }
  return /* @__PURE__ */ jsxs19("div", { className: "space-y-4", children: [
    mode === "blur" && /* @__PURE__ */ jsxs19("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs19("div", { className: "flex items-center justify-between text-xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx36("span", { children: "Unsch\xE4rfe" }),
        /* @__PURE__ */ jsx36("span", { children: blurRadius })
      ] }),
      /* @__PURE__ */ jsx36(
        "input",
        {
          type: "range",
          min: 1,
          max: 20,
          value: blurRadius,
          onChange: (e) => onBlurRadiusChange(parseInt(e.target.value, 10)),
          className: "w-full accent-primary"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs19("div", { className: "grid grid-cols-3 gap-2", children: [
      /* @__PURE__ */ jsx36(
        PickerOption,
        {
          selected: mode === "none",
          onClick: onSelectNone,
          isProcessing,
          label: "Keiner",
          children: /* @__PURE__ */ jsx36(Ban, { className: "w-6 h-6 text-muted-foreground" })
        }
      ),
      /* @__PURE__ */ jsx36(
        PickerOption,
        {
          selected: mode === "blur",
          onClick: () => onSelectBlur(),
          isProcessing,
          label: "Weichzeichnen",
          children: /* @__PURE__ */ jsx36(Sparkles, { className: "w-6 h-6 text-muted-foreground" })
        }
      ),
      presets.map((preset) => /* @__PURE__ */ jsx36(
        PickerOption,
        {
          selected: mode === "image" && selectedPreset === preset.id,
          onClick: () => onSelectImage(preset.id),
          isProcessing,
          label: preset.name,
          children: /* @__PURE__ */ jsx36(
            "img",
            {
              src: preset.thumbnail,
              alt: preset.name,
              className: "w-full h-full object-cover"
            }
          )
        },
        preset.id
      ))
    ] }),
    isProcessing && /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx36(Loader25, { className: "w-3 h-3 animate-spin" }),
      "Wird angewendet..."
    ] })
  ] });
}
function PickerOption({
  selected,
  onClick,
  isProcessing,
  label,
  children
}) {
  return /* @__PURE__ */ jsxs19(
    "button",
    {
      type: "button",
      onClick,
      disabled: isProcessing,
      className: cn(
        "relative aspect-4/3 rounded-lg border-2 flex items-center justify-center overflow-hidden transition-all",
        "hover:border-primary/50 hover:scale-[1.02] disabled:opacity-50",
        selected ? "border-primary ring-1 ring-primary/30" : "border-border"
      ),
      title: label,
      children: [
        children,
        selected && /* @__PURE__ */ jsx36("div", { className: "absolute inset-0 flex items-center justify-center bg-black/30", children: /* @__PURE__ */ jsx36("div", { className: "w-6 h-6 rounded-full bg-primary flex items-center justify-center", children: /* @__PURE__ */ jsx36(Check3, { className: "w-3.5 h-3.5 text-primary-foreground" }) }) })
      ]
    }
  );
}

// src/components/video/backgroundPresets.ts
import { useMemo as useMemo3 } from "react";
var DEFAULT_BACKGROUND_PRESETS = [];
function useVideoBackgrounds(queryResult) {
  return useMemo3(() => {
    if (!queryResult || queryResult.length === 0) {
      return [];
    }
    return queryResult.filter((bg) => bg.url !== null).map((bg) => ({
      id: bg._id,
      name: bg.name,
      thumbnail: bg.url,
      imagePath: bg.url
    }));
  }, [queryResult]);
}

// src/components/video/hooks/useVideoRoom.ts
import { useState as useState9, useCallback as useCallback3, useEffect as useEffect7, useRef as useRef4 } from "react";
import {
  Room,
  RoomEvent,
  ConnectionState,
  ConnectionQuality,
  ExternalE2EEKeyProvider,
  VideoPresets
} from "livekit-client";
function safeDisconnectRoom(targetRoom) {
  if (!targetRoom) return;
  try {
    targetRoom.disconnect();
  } catch (err) {
    console.warn("Room disconnect failed during cleanup:", err);
  }
}
function useVideoRoom(options) {
  const {
    serverUrl,
    token,
    role,
    forceLegacySignalPath = true,
    e2ee,
    preferHighQuality = false,
    onParticipantJoined,
    onParticipantLeft,
    onDisconnected,
    onE2EEStatusChanged
  } = options;
  const [room, setRoom] = useState9(null);
  const [connectionState, setConnectionState] = useState9("disconnected");
  const [remoteParticipants, setRemoteParticipants] = useState9([]);
  const [error, setError] = useState9(null);
  const [isE2EEEnabled, setIsE2EEEnabled] = useState9(false);
  const [connectionQuality, setConnectionQuality] = useState9("unknown");
  const keyProviderRef = useRef4(null);
  const e2eeWorkerRef = useRef4(null);
  const generationRef = useRef4(0);
  const roomRef = useRef4(null);
  const getSpotlightParticipant = useCallback3(
    (participants) => {
      if (participants.length === 0) return null;
      const targetRole = role === "gutachter" ? "versicherter" : "gutachter";
      const spotlight = participants.find((p) => {
        try {
          const metadata = JSON.parse(p.metadata || "{}");
          return metadata.role === targetRole;
        } catch {
          return false;
        }
      });
      return spotlight || participants[0];
    },
    [role]
  );
  const connect = useCallback3(async () => {
    if (roomRef.current?.state === ConnectionState.Connected) {
      return;
    }
    const myGeneration = generationRef.current;
    setConnectionState("connecting");
    setError(null);
    setIsE2EEEnabled(false);
    if (roomRef.current) {
      safeDisconnectRoom(roomRef.current);
      roomRef.current = null;
    }
    try {
      let keyProvider;
      let e2eeWorker;
      if (e2ee?.sharedKey) {
        keyProvider = new ExternalE2EEKeyProvider();
        keyProviderRef.current = keyProvider;
        const keyMaterial = new TextEncoder().encode(e2ee.sharedKey.padEnd(32, "0"));
        await keyProvider.setKey(keyMaterial.buffer);
        const workerUrl = e2ee.workerUrl || "/livekit-e2ee-worker.js";
        if (e2eeWorkerRef.current) {
          e2eeWorkerRef.current.terminate();
          e2eeWorkerRef.current = null;
        }
        try {
          e2eeWorker = new Worker(workerUrl, { type: "module" });
          e2eeWorkerRef.current = e2eeWorker;
          console.log("E2EE worker loaded from:", workerUrl);
        } catch (workerError) {
          console.warn("E2EE worker load failed:", workerError);
          try {
            e2eeWorker = new Worker(
              new URL("livekit-client/e2ee-worker", import.meta.url),
              { type: "module" }
            );
            e2eeWorkerRef.current = e2eeWorker;
            console.log("E2EE worker loaded via dynamic import");
          } catch (fallbackError) {
            console.error("E2EE worker failed to load:", fallbackError);
          }
        }
      }
      const roomOptions = {
        adaptiveStream: true,
        dynacast: true,
        // Capture at 1080p/30fps for maximum clarity in medical assessments.
        // Adaptive streaming + simulcast will downscale automatically on
        // poor connections.
        videoCaptureDefaults: {
          resolution: VideoPresets.h1080.resolution
        },
        // Custom high-bitrate 1080p encoding (6 Mbps) for crisp medical
        // assessment video. The default h1080 preset only allows 3 Mbps
        // which is too conservative. VP9 + simulcast ensure graceful
        // degradation on poor connections.
        publishDefaults: {
          videoEncoding: {
            maxBitrate: 6e6,
            maxFramerate: 30
          },
          videoCodec: "vp9",
          simulcast: true,
          screenShareEncoding: {
            maxBitrate: 8e6,
            maxFramerate: 30
          }
        }
      };
      if (keyProvider && e2eeWorker) {
        roomOptions.e2ee = {
          keyProvider,
          worker: e2eeWorker
        };
      }
      const newRoom = new Room(roomOptions);
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }
      newRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
        if (generationRef.current !== myGeneration) return;
        switch (state) {
          case ConnectionState.Connected:
            setConnectionState("connected");
            break;
          case ConnectionState.Reconnecting:
            setConnectionState("reconnecting");
            break;
          case ConnectionState.Disconnected:
            setConnectionState("disconnected");
            setIsE2EEEnabled(false);
            onDisconnected?.();
            break;
        }
      });
      newRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        if (generationRef.current !== myGeneration) return;
        setRemoteParticipants((prev) => [...prev, participant]);
        onParticipantJoined?.(participant);
      });
      newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (generationRef.current !== myGeneration) return;
        setRemoteParticipants(
          (prev) => prev.filter((p) => p.sid !== participant.sid)
        );
        onParticipantLeft?.(participant);
      });
      newRoom.on(RoomEvent.Disconnected, () => {
        if (generationRef.current !== myGeneration) return;
        setConnectionState("disconnected");
        setRemoteParticipants([]);
        setIsE2EEEnabled(false);
        onDisconnected?.();
      });
      newRoom.on(RoomEvent.EncryptionError, (err) => {
        if (generationRef.current !== myGeneration) return;
        console.error("E2EE encryption error:", err);
        setError(new Error(`E2EE error: ${err.message}`));
      });
      newRoom.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        if (generationRef.current !== myGeneration) return;
        if (participant.isLocal) {
          switch (quality) {
            case ConnectionQuality.Excellent:
              setConnectionQuality("excellent");
              break;
            case ConnectionQuality.Good:
              setConnectionQuality("good");
              break;
            case ConnectionQuality.Poor:
              setConnectionQuality("poor");
              break;
            default:
              setConnectionQuality("unknown");
          }
        }
      });
      await newRoom.connect(serverUrl, token, {
        autoSubscribe: true
      });
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }
      if (e2ee?.sharedKey && keyProvider) {
        try {
          await newRoom.setE2EEEnabled(true);
          setIsE2EEEnabled(true);
          onE2EEStatusChanged?.(true);
          console.log("E2E encryption enabled successfully");
        } catch (e2eeError) {
          console.error("Failed to enable E2EE:", e2eeError);
          setError(new Error(`E2EE activation failed: ${e2eeError instanceof Error ? e2eeError.message : "Unknown error"}`));
        }
      }
      await newRoom.localParticipant.setMetadata(JSON.stringify({ role }));
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }
      const participants = Array.from(newRoom.remoteParticipants.values());
      setRemoteParticipants(participants);
      roomRef.current = newRoom;
      setRoom(newRoom);
      setConnectionState("connected");
    } catch (err) {
      if (generationRef.current !== myGeneration) return;
      setError(err instanceof Error ? err : new Error("Connection failed"));
      setConnectionState("disconnected");
      setIsE2EEEnabled(false);
    }
  }, [serverUrl, token, role, forceLegacySignalPath, e2ee?.sharedKey, preferHighQuality, onParticipantJoined, onParticipantLeft, onDisconnected, onE2EEStatusChanged]);
  const disconnect = useCallback3(() => {
    generationRef.current++;
    safeDisconnectRoom(roomRef.current);
    roomRef.current = null;
    if (e2eeWorkerRef.current) {
      e2eeWorkerRef.current.terminate();
      e2eeWorkerRef.current = null;
    }
    setRoom(null);
    setConnectionState("disconnected");
    setConnectionQuality("unknown");
    setIsE2EEEnabled(false);
    setRemoteParticipants([]);
    onDisconnected?.();
  }, [onDisconnected]);
  useEffect7(() => {
    const teardown = () => {
      generationRef.current++;
      safeDisconnectRoom(roomRef.current);
      roomRef.current = null;
      if (e2eeWorkerRef.current) {
        e2eeWorkerRef.current.terminate();
        e2eeWorkerRef.current = null;
      }
    };
    window.addEventListener("beforeunload", teardown);
    return () => {
      window.removeEventListener("beforeunload", teardown);
      teardown();
    };
  }, []);
  return {
    room,
    connectionState,
    connectionQuality,
    localParticipant: room?.localParticipant || null,
    remoteParticipants,
    spotlightParticipant: getSpotlightParticipant(remoteParticipants),
    isE2EEEnabled,
    connect,
    disconnect,
    error
  };
}

// src/components/video/hooks/useMediaControls.ts
import { useState as useState10, useCallback as useCallback4 } from "react";
var STORAGE_KEY_MIC = "jourfx:video:mic";
var STORAGE_KEY_CAMERA = "jourfx:video:camera";
var STORAGE_KEY_DEVICE_CAMERA = "jourfx:video:device:camera";
var STORAGE_KEY_DEVICE_MIC = "jourfx:video:device:microphone";
var STORAGE_KEY_DEVICE_SPEAKER = "jourfx:video:device:speaker";
function loadBool(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v === "true") return true;
    if (v === "false") return false;
  } catch {
  }
  return fallback;
}
function saveBool(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
  }
}
var DEVICE_STORAGE_KEYS = {
  camera: STORAGE_KEY_DEVICE_CAMERA,
  microphone: STORAGE_KEY_DEVICE_MIC,
  speaker: STORAGE_KEY_DEVICE_SPEAKER
};
function saveDevicePreference(kind, deviceId) {
  try {
    localStorage.setItem(DEVICE_STORAGE_KEYS[kind], deviceId);
  } catch {
  }
}
function loadDevicePreference(kind) {
  try {
    return localStorage.getItem(DEVICE_STORAGE_KEYS[kind]) || "";
  } catch {
    return "";
  }
}
function getPreferredDeviceId(kind, availableDeviceIds, activeDeviceId) {
  const preferred = loadDevicePreference(kind);
  if (preferred && availableDeviceIds.includes(preferred)) return preferred;
  if (activeDeviceId && availableDeviceIds.includes(activeDeviceId)) return activeDeviceId;
  return availableDeviceIds[0] || "";
}
function useMediaControls(options) {
  const { room, onToggleCamera } = options;
  const [isMicEnabled, setIsMicEnabled] = useState10(() => loadBool(STORAGE_KEY_MIC, true));
  const [isCameraEnabled, setIsCameraEnabled] = useState10(() => loadBool(STORAGE_KEY_CAMERA, true));
  const [isScreenSharing, setIsScreenSharing] = useState10(false);
  const [microphoneError, setMicrophoneError] = useState10(null);
  const [cameraError, setCameraError] = useState10(null);
  const [screenShareError, setScreenShareError] = useState10(null);
  const toggleMic = useCallback4(async () => {
    if (!room?.localParticipant) return;
    try {
      setMicrophoneError(null);
      const newState = !isMicEnabled;
      await room.localParticipant.setMicrophoneEnabled(newState);
      setIsMicEnabled(newState);
      saveBool(STORAGE_KEY_MIC, newState);
    } catch (err) {
      setMicrophoneError(
        err instanceof Error ? err : new Error("Microphone error")
      );
    }
  }, [room, isMicEnabled]);
  const toggleCamera = useCallback4(async () => {
    if (!room?.localParticipant) return;
    try {
      setCameraError(null);
      const newState = !isCameraEnabled;
      if (onToggleCamera) {
        await onToggleCamera(newState, room.localParticipant);
      } else {
        await room.localParticipant.setCameraEnabled(newState);
      }
      setIsCameraEnabled(newState);
      saveBool(STORAGE_KEY_CAMERA, newState);
    } catch (err) {
      setCameraError(err instanceof Error ? err : new Error("Camera error"));
    }
  }, [room, isCameraEnabled, onToggleCamera]);
  const toggleScreenShare = useCallback4(async () => {
    if (!room?.localParticipant) return;
    try {
      setScreenShareError(null);
      const newState = !isScreenSharing;
      await room.localParticipant.setScreenShareEnabled(newState);
      setIsScreenSharing(newState);
    } catch (err) {
      setScreenShareError(
        err instanceof Error ? err : new Error("Screen share error")
      );
    }
  }, [room, isScreenSharing]);
  return {
    isMicEnabled,
    isCameraEnabled,
    isScreenSharing,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    microphoneError,
    cameraError,
    screenShareError
  };
}

// src/components/video/hooks/useReconnection.ts
import { useState as useState11, useCallback as useCallback5, useEffect as useEffect8, useRef as useRef5 } from "react";
import { RoomEvent as RoomEvent2, ConnectionState as ConnectionState2 } from "livekit-client";
var DEFAULT_CONFIG = {
  initialDelayMs: 500,
  maxDelayMs: 1e4,
  maxAttempts: 15,
  backoffMultiplier: 1.5,
  jitterMs: 200
};
function useReconnection(options) {
  const { room, config: configOverrides, onReconnectFailed } = options;
  const config = { ...DEFAULT_CONFIG, ...configOverrides };
  const [isReconnecting, setIsReconnecting] = useState11(false);
  const [attempt, setAttempt] = useState11(0);
  const [canManualRetry, setCanManualRetry] = useState11(false);
  const [lastError, setLastError] = useState11(null);
  const [timeUntilNextAttempt] = useState11(null);
  const timerRef = useRef5(null);
  const saveLocalState = useCallback5(() => {
    if (!room?.localParticipant) return;
    const state = {
      audioEnabled: room.localParticipant.isMicrophoneEnabled,
      videoEnabled: room.localParticipant.isCameraEnabled,
      timestamp: Date.now()
    };
    sessionStorage.setItem("videoRoomState", JSON.stringify(state));
  }, [room]);
  useEffect8(() => {
    if (!room) return;
    const handleReconnecting = () => {
      setIsReconnecting(true);
      setAttempt((prev) => prev + 1);
      saveLocalState();
    };
    const handleReconnected = () => {
      setIsReconnecting(false);
      setAttempt(0);
      setLastError(null);
      setCanManualRetry(false);
    };
    const handleDisconnected = () => {
      if (attempt >= config.maxAttempts) {
        setIsReconnecting(false);
        setCanManualRetry(true);
        setLastError("Verbindung fehlgeschlagen nach " + config.maxAttempts + " Versuchen");
        onReconnectFailed?.();
      }
    };
    room.on(RoomEvent2.Reconnecting, handleReconnecting);
    room.on(RoomEvent2.Reconnected, handleReconnected);
    room.on(RoomEvent2.Disconnected, handleDisconnected);
    return () => {
      room.off(RoomEvent2.Reconnecting, handleReconnecting);
      room.off(RoomEvent2.Reconnected, handleReconnected);
      room.off(RoomEvent2.Disconnected, handleDisconnected);
    };
  }, [room, attempt, config.maxAttempts, saveLocalState, onReconnectFailed]);
  const manualReconnect = useCallback5(async () => {
    if (!room) return;
    setIsReconnecting(true);
    setAttempt(1);
    setCanManualRetry(false);
    setLastError(null);
    try {
      if (room.state === ConnectionState2.Disconnected) {
        setLastError("Bitte laden Sie die Seite neu f\xFCr eine neue Verbindung");
        setCanManualRetry(true);
      }
    } catch {
      setLastError("Wiederverbindung fehlgeschlagen");
      setCanManualRetry(true);
    } finally {
      setIsReconnecting(false);
    }
  }, [room]);
  useEffect8(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  return {
    isReconnecting,
    attempt,
    maxAttempts: config.maxAttempts,
    canManualRetry,
    manualReconnect,
    lastError,
    timeUntilNextAttempt
  };
}

// src/components/video/hooks/useBackgroundProcessor.ts
import { useState as useState12, useCallback as useCallback6, useEffect as useEffect9, useRef as useRef6 } from "react";
import { RoomEvent as RoomEvent3, LocalVideoTrack, Track as Track3, ConnectionState as ConnectionState3, ConnectionQuality as ConnectionQuality2 } from "livekit-client";
import { BackgroundProcessor, supportsBackgroundProcessors } from "@livekit/track-processors";
var STORAGE_PREFIX = "jourfx:background";
var LANDSCAPE_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite";
var SEGMENTER_OPTS = {
  segmenterOptions: { delegate: "GPU" },
  assetPaths: { modelAssetPath: LANDSCAPE_MODEL_URL },
  // Cap fallback (canvas.captureStream) pipeline at 24 fps to reduce GPU load
  maxFps: 24
};
var DEGRADED_CAMERA_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 24, max: 24 }
};
var MAX_BG_WIDTH = 640;
var MAX_BG_HEIGHT = 360;
var IMAGE_LOAD_TIMEOUT_MS = 1e4;
async function preloadBackgroundImage(url, signal) {
  const response = await fetch(url, { signal, mode: "cors" });
  if (!response.ok) {
    throw new Error(`Failed to fetch background image: ${response.status}`);
  }
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  let { width, height } = bitmap;
  if (width > MAX_BG_WIDTH || height > MAX_BG_HEIGHT) {
    const scale = Math.min(MAX_BG_WIDTH / width, MAX_BG_HEIGHT / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const resizedBlob = await canvas.convertToBlob({
    type: "image/jpeg",
    quality: 0.85
  });
  return URL.createObjectURL(resizedBlob);
}
function loadSavedMode() {
  try {
    const saved = localStorage.getItem(`${STORAGE_PREFIX}:mode`);
    if (saved === "blur" || saved === "image" || saved === "none") return saved;
  } catch {
  }
  return "none";
}
function loadSavedBlurRadius() {
  try {
    const saved = localStorage.getItem(`${STORAGE_PREFIX}:blur`);
    if (saved) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n > 0 && n <= 20) return n;
    }
  } catch {
  }
  return 10;
}
function loadSavedPreset() {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}:preset`);
  } catch {
  }
  return null;
}
function savePreference(mode, blurRadius, presetId) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}:mode`, mode);
    localStorage.setItem(`${STORAGE_PREFIX}:blur`, String(blurRadius));
    if (presetId) {
      localStorage.setItem(`${STORAGE_PREFIX}:preset`, presetId);
    } else {
      localStorage.removeItem(`${STORAGE_PREFIX}:preset`);
    }
  } catch {
  }
}
async function constrainCameraForPoorConnection(videoTrack) {
  try {
    const settings = videoTrack.mediaStreamTrack.getSettings();
    if (settings.height && settings.height > 720) {
      await videoTrack.mediaStreamTrack.applyConstraints(DEGRADED_CAMERA_CONSTRAINTS);
    }
  } catch {
  }
}
async function unconstrainCamera(videoTrack) {
  try {
    await videoTrack.mediaStreamTrack.applyConstraints({});
  } catch {
  }
}
function useBackgroundProcessor(options) {
  const { room, track, presets = [] } = options;
  const [mode, setMode] = useState12(loadSavedMode);
  const [blurRadius, setBlurRadius] = useState12(loadSavedBlurRadius);
  const [selectedPreset, setSelectedPreset] = useState12(loadSavedPreset);
  const [isProcessing, setIsProcessing] = useState12(false);
  const [error, setError] = useState12(null);
  const processorRef = useRef6(null);
  const retryRef = useRef6(0);
  const isSupported = supportsBackgroundProcessors();
  const imageAbortRef = useRef6(null);
  const blobUrlRef = useRef6(null);
  const revokeBlobUrl = useCallback6(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);
  const getVideoTrack = useCallback6(() => {
    if (track) return track;
    if (!room?.localParticipant) return null;
    const pub = room.localParticipant.getTrackPublication(Track3.Source.Camera);
    if (pub?.track && pub.track instanceof LocalVideoTrack) {
      return pub.track;
    }
    return null;
  }, [room, track]);
  const prepareImageUrl = useCallback6(
    async (remoteUrl) => {
      imageAbortRef.current?.abort();
      revokeBlobUrl();
      const controller = new AbortController();
      imageAbortRef.current = controller;
      const timeoutId = setTimeout(
        () => controller.abort(),
        IMAGE_LOAD_TIMEOUT_MS
      );
      try {
        const blobUrl = await preloadBackgroundImage(
          remoteUrl,
          controller.signal
        );
        blobUrlRef.current = blobUrl;
        return blobUrl;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [revokeBlobUrl]
  );
  const applyProcessor = useCallback6(
    async (newMode, radius, presetId) => {
      if (!isSupported) return;
      const videoTrack = getVideoTrack();
      if (!videoTrack || videoTrack.mediaStreamTrack?.readyState !== "live") {
        if (retryRef.current < 3) {
          retryRef.current++;
          setTimeout(() => applyProcessor(newMode, radius, presetId), 500);
        }
        return;
      }
      retryRef.current = 0;
      setIsProcessing(true);
      setError(null);
      try {
        if (newMode === "none") {
          imageAbortRef.current?.abort();
          revokeBlobUrl();
          if (processorRef.current) {
            await videoTrack.stopProcessor();
            processorRef.current = null;
          }
        } else if (newMode === "blur") {
          imageAbortRef.current?.abort();
          revokeBlobUrl();
          if (processorRef.current) {
            await processorRef.current.switchTo({
              mode: "background-blur",
              blurRadius: radius
            });
          } else {
            const processor = BackgroundProcessor({
              mode: "background-blur",
              blurRadius: radius,
              ...SEGMENTER_OPTS
            });
            await videoTrack.setProcessor(processor);
            processorRef.current = processor;
          }
        } else if (newMode === "image") {
          const preset = presets.find((p) => p.id === presetId);
          if (!preset) return;
          const localUrl = await prepareImageUrl(preset.imagePath);
          if (processorRef.current) {
            await processorRef.current.switchTo({
              mode: "virtual-background",
              imagePath: localUrl
            });
          } else {
            const processor = BackgroundProcessor({
              mode: "virtual-background",
              imagePath: localUrl,
              ...SEGMENTER_OPTS
            });
            await videoTrack.setProcessor(processor);
            processorRef.current = processor;
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err : new Error("Background processor error"));
      } finally {
        setIsProcessing(false);
      }
    },
    [isSupported, getVideoTrack, presets, prepareImageUrl, revokeBlobUrl]
  );
  const setBlur = useCallback6(
    async (radius) => {
      const r = radius ?? blurRadius;
      setMode("blur");
      setBlurRadius(r);
      setSelectedPreset(null);
      savePreference("blur", r, null);
      await applyProcessor("blur", r, null);
    },
    [blurRadius, applyProcessor]
  );
  const setImage = useCallback6(
    async (presetId) => {
      setMode("image");
      setSelectedPreset(presetId);
      savePreference("image", blurRadius, presetId);
      await applyProcessor("image", blurRadius, presetId);
    },
    [blurRadius, applyProcessor]
  );
  const disable = useCallback6(async () => {
    setMode("none");
    setSelectedPreset(null);
    savePreference("none", blurRadius, null);
    await applyProcessor("none", blurRadius, null);
  }, [blurRadius, applyProcessor]);
  const applyToUnpublishedTrack = useCallback6(
    async (videoTrack) => {
      if (!isSupported || mode === "none") return false;
      try {
        if (mode === "blur") {
          const processor = BackgroundProcessor({
            mode: "background-blur",
            blurRadius,
            ...SEGMENTER_OPTS
          });
          await videoTrack.setProcessor(processor);
          processorRef.current = processor;
          return true;
        } else if (mode === "image") {
          const preset = presets.find((p) => p.id === selectedPreset);
          if (!preset) return false;
          const localUrl = await prepareImageUrl(preset.imagePath);
          const processor = BackgroundProcessor({
            mode: "virtual-background",
            imagePath: localUrl,
            ...SEGMENTER_OPTS
          });
          await videoTrack.setProcessor(processor);
          processorRef.current = processor;
          return true;
        }
      } catch (err) {
        console.error("Failed to pre-apply background processor:", err);
      }
      return false;
    },
    [isSupported, mode, blurRadius, selectedPreset, presets, prepareImageUrl]
  );
  useEffect9(() => {
    if (!room || track || mode === "none") return;
    const handleTrackPublished = () => {
      if (processorRef.current) return;
      setTimeout(() => {
        applyProcessor(mode, blurRadius, selectedPreset);
      }, 200);
    };
    room.on(RoomEvent3.LocalTrackPublished, handleTrackPublished);
    return () => {
      room.off(RoomEvent3.LocalTrackPublished, handleTrackPublished);
    };
  }, [room, track, mode, blurRadius, selectedPreset, applyProcessor]);
  useEffect9(() => {
    if (!room || track || mode === "none") return;
    const handleConnected = () => {
      if (processorRef.current) return;
      setTimeout(() => {
        applyProcessor(mode, blurRadius, selectedPreset);
      }, 500);
    };
    room.on(RoomEvent3.Connected, handleConnected);
    if (room.state === ConnectionState3.Connected) {
      handleConnected();
    }
    return () => {
      room.off(RoomEvent3.Connected, handleConnected);
    };
  }, [room, track, mode, blurRadius, selectedPreset, applyProcessor]);
  const isDegradedRef = useRef6(false);
  useEffect9(() => {
    if (!room || mode === "none") return;
    const handleQualityChange = (quality) => {
      const videoTrack = getVideoTrack();
      if (!videoTrack) return;
      if (quality === ConnectionQuality2.Poor && !isDegradedRef.current) {
        isDegradedRef.current = true;
        constrainCameraForPoorConnection(videoTrack).catch(() => {
        });
      } else if (quality !== ConnectionQuality2.Poor && isDegradedRef.current) {
        isDegradedRef.current = false;
        unconstrainCamera(videoTrack).catch(() => {
        });
      }
    };
    room.on(RoomEvent3.ConnectionQualityChanged, handleQualityChange);
    return () => {
      room.off(RoomEvent3.ConnectionQualityChanged, handleQualityChange);
      if (isDegradedRef.current) {
        isDegradedRef.current = false;
        const videoTrack = getVideoTrack();
        if (videoTrack) unconstrainCamera(videoTrack).catch(() => {
        });
      }
    };
  }, [room, mode, getVideoTrack]);
  useEffect9(() => {
    const cleanup = () => {
      imageAbortRef.current?.abort();
      revokeBlobUrl();
      const videoTrack = getVideoTrack();
      if (videoTrack && processorRef.current) {
        videoTrack.stopProcessor().catch(() => {
        });
        processorRef.current = null;
      }
    };
    window.addEventListener("beforeunload", cleanup);
    return () => {
      window.removeEventListener("beforeunload", cleanup);
      cleanup();
    };
  }, [getVideoTrack, revokeBlobUrl]);
  return {
    mode,
    blurRadius,
    selectedPreset,
    isSupported,
    isProcessing,
    error,
    setBlur,
    setImage,
    disable,
    applyToUnpublishedTrack
  };
}
export {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarUpload,
  BackgroundPicker,
  Badge,
  BirthdayPicker,
  Button,
  Calendar,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent2 as CollapsibleContent,
  CollapsibleTrigger2 as CollapsibleTrigger,
  ConnectionStatus,
  ControlBar,
  DEFAULT_BACKGROUND_PRESETS,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  ErrorBoundary,
  ErrorFallback,
  Input,
  Label,
  ParticipantTile,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Progress,
  ReconnectionOverlay,
  ScrollArea,
  ScrollBar,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Skeleton,
  SkipLink,
  Switch,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  VideoErrorBoundary,
  VideoLoadingState,
  VideoStage,
  alertVariants,
  badgeVariants,
  buttonVariants,
  cn,
  getPreferredDeviceId,
  loadDevicePreference,
  saveDevicePreference,
  toast,
  useBackgroundProcessor,
  useIsMobile,
  useMediaControls,
  useReconnection,
  useSidebar,
  useToast,
  useVideoBackgrounds,
  useVideoRoom
};
